<template>
  <div class="spf">
    <template v-if="loaded">
      <SearchSelect
        v-if="fields" v-model="fields" multiple :max="MAX_FIELDS" :options="options" :disabled="disabled"
        placeholder="Select phone fields…"
      />
      <p v-else class="spf-error">Couldn’t load your phone number fields. Refresh the page to try again.</p>
    </template>
    <AutosaveBadge :status="status" :error-message="errorMessage" @retry="retry" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import SearchSelect from '../../../components/SearchSelect.vue'
import AutosaveBadge from './AutosaveBadge.vue'
import { apiFetch } from '../../../services/api'
import { getSalesforceFieldIconKey } from '../../../services/iconRegistry'
import { useAutosave } from '../../composables/useAutosave'
import { logIntegrationFetchError } from './log'

interface CrmField { name: string; label: string; type: string }
type SfObject = 'Contact' | 'Lead'
type PhoneFields = Record<SfObject, string[]>

const MAX_FIELDS = 10

defineProps<{ disabled?: boolean }>()

// One list for both objects. Null unless the lists and both objects' fields loaded: each field is saved
// for the objects that have it, so a partial read would drop some.
const fields = ref<string[] | null>(null)
const described = reactive<Record<SfObject, CrmField[]>>({ Contact: [], Lead: [] })
const loaded = ref(false)
let saved = ''

const { status, errorMessage, schedule, retry } = useAutosave(save)

// Contact's label wins for a field on both objects.
const options = computed(() => {
  const seen = new Set<string>()
  const out: { value: string; label: string; secondaryLabel: string; iconKey: string | null }[] = []
  for (const f of [...described.Contact, ...described.Lead]) {
    const key = f.name.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ value: f.name, label: f.label || f.name, secondaryLabel: f.name, iconKey: getSalesforceFieldIconKey(f.type) })
  }
  return out
})

function body(): PhoneFields | null {
  const chosen = fields.value
  if (!chosen) return null
  const on = (object: SfObject) => {
    const names = new Set(described[object].map((f) => f.name.toLowerCase()))
    return chosen.filter((f) => names.has(f.toLowerCase()))
  }
  return { Contact: on('Contact'), Lead: on('Lead') }
}

watch(() => JSON.stringify(body()), (cur) => { if (fields.value && cur !== saved) schedule() })

// The endpoint answers 200 with no fields when it can't describe the object; both objects always have fields.
function describe(object: SfObject): Promise<CrmField[] | null> {
  return apiFetch<{ fields: CrmField[] }>(`/settings/integrations/salesforce/data_fields?object_name=${object}`)
    .then((r) => (r.fields.length ? r.fields : null))
    .catch((err) => { logIntegrationFetchError('Salesforce', `load ${object} data fields`, err); return null })
}

async function load() {
  const [stored, contact, lead] = await Promise.all([
    apiFetch<PhoneFields>('/crm/salesforce/phone-fields')
      .catch((err) => { logIntegrationFetchError('Salesforce', 'load phone fields', err); return null }),
    describe('Contact'),
    describe('Lead'),
  ])
  if (stored && contact && lead) {
    described.Contact = contact
    described.Lead = lead
    // In Salesforce's own spelling where it has the field.
    const canonical = new Map([...contact, ...lead].map((f) => [f.name.toLowerCase(), f.name]))
    const union = [...stored.Contact, ...stored.Lead].map((f) => canonical.get(f.toLowerCase()) ?? f)
    fields.value = union.filter((f, i) => union.findIndex((g) => g.toLowerCase() === f.toLowerCase()) === i)
    saved = JSON.stringify(body())
  }
  loaded.value = true
}

async function save() {
  const lists = body()
  const json = JSON.stringify(lists)
  if (!lists || json === saved) return
  await apiFetch('/crm/salesforce/phone-fields', { method: 'PUT', body: lists })
  saved = json
}

onMounted(load)
</script>

<style scoped>
.spf { width: 100%; max-width: 520px; display: flex; flex-direction: column; align-items: flex-start; gap: var(--space-2); }
.spf-error { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-tertiary); }
</style>
